import getStroke from 'perfect-freehand';

import { DOODLE_CANVAS, DOODLE_CANVAS_BG } from './constants';
import type { DoodleStroke } from './use-doodle';

const STROKE_OPTIONS_BASE = {
	thinning: 0.6,
	smoothing: 0.5,
	streamline: 0.5,
	easing: (t: number) => Math.sin((t * Math.PI) / 2),
	last: true,
};

function average(a: number, b: number): number {
	return (a + b) / 2;
}

/**
 * True when a point is a usable `[x, y, pressure?]` tuple. Backend-supplied
 * strokes can arrive malformed (non-array points, short tuples, non-finite
 * numbers); reading `point[0]`/`point[1]` on those throws deep inside the
 * renderer, so we screen them out defensively at every entry point.
 */
function isValidPoint(point: unknown): point is number[] {
	return Array.isArray(point) && point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]);
}

/** The subset of a stroke's points that are safe to render. */
function validPoints(stroke: DoodleStroke): number[][] {
	if (!Array.isArray(stroke.points)) return [];
	return stroke.points.filter(isValidPoint);
}

export function getSvgPathFromStroke(points: number[][]): string {
	const len = points.length;
	if (len < 4) return '';

	let a = points[0];
	let b = points[1];
	const c = points[2];

	let result = `M${a[0].toFixed(2)},${a[1].toFixed(2)} Q${b[0].toFixed(2)},${b[1].toFixed(2)} ${average(b[0], c[0]).toFixed(2)},${average(b[1], c[1]).toFixed(2)} T`;

	for (let i = 2, max = len - 1; i < max; i++) {
		a = points[i];
		b = points[i + 1];
		result += `${average(a[0], b[0]).toFixed(2)},${average(a[1], b[1]).toFixed(2)} `;
	}

	result += 'Z';
	return result;
}

export function getStrokeOutline(stroke: DoodleStroke): number[][] {
	const points = validPoints(stroke);
	if (points.length === 0) return [];

	const simulatePressure = !points.some((p) => p.length >= 3 && p[2] !== 0.5);

	return getStroke(points, {
		...STROKE_OPTIONS_BASE,
		size: stroke.size * 4.25,
		simulatePressure,
	});
}

/**
 * True when a single stroke would actually paint pixels: a fill with a valid
 * seed point, or a brush/eraser stroke whose outline yields a non-empty SVG
 * path. A single tap or a two-point flick produces an outline of <4 points and
 * renders nothing — those must NOT count as content, or "Add to draft"/Save
 * would upload a blank white PNG.
 */
export function strokeHasRenderedContent(stroke: DoodleStroke): boolean {
	if (stroke.tool === 'fill') {
		return validPoints(stroke).length > 0;
	}
	return getSvgPathFromStroke(getStrokeOutline(stroke)) !== '';
}

/**
 * True when at least one stroke in the set would paint pixels. Gate the modal
 * Save-enable and the card's "Add to draft" guard on THIS, not `strokes.length`
 * — a doodle made only of degenerate taps/flicks has a non-zero array length
 * but rasterizes to an all-white PNG.
 */
export function hasRenderedContent(strokes: DoodleStroke[]): boolean {
	return strokes.some(strokeHasRenderedContent);
}

export function renderStrokeToCtx(ctx: CanvasRenderingContext2D, stroke: DoodleStroke): void {
	if (stroke.tool === 'fill') {
		const points = validPoints(stroke);
		if (points.length === 0) return;
		floodFill(ctx, Math.round(points[0][0]), Math.round(points[0][1]), stroke.color, ctx.canvas.width, ctx.canvas.height);
		return;
	}

	const outline = getStrokeOutline(stroke);
	const pathData = getSvgPathFromStroke(outline);
	if (!pathData) return;

	const path = new Path2D(pathData);
	// The eraser has no true "clear" — it paints the canvas background back over
	// existing strokes (see DOODLE_CANVAS_BG).
	ctx.fillStyle = stroke.tool === 'eraser' ? DOODLE_CANVAS_BG : stroke.color;
	ctx.fill(path);
}

function hexToRgba(hex: string): [number, number, number, number] {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return [r, g, b, 255];
}

function colorsMatch(data: Uint8ClampedArray, idx: number, target: [number, number, number, number], tolerance: number): boolean {
	return (
		Math.abs(data[idx] - target[0]) <= tolerance &&
		Math.abs(data[idx + 1] - target[1]) <= tolerance &&
		Math.abs(data[idx + 2] - target[2]) <= tolerance &&
		Math.abs(data[idx + 3] - target[3]) <= tolerance
	);
}

function floodFill(ctx: CanvasRenderingContext2D, startX: number, startY: number, fillHex: string, width: number, height: number): void {
	if (startX < 0 || startX >= width || startY < 0 || startY >= height) return;

	const imageData = ctx.getImageData(0, 0, width, height);
	const data = imageData.data;
	const fill = hexToRgba(fillHex);
	const tolerance = 32;

	const startIdx = (startY * width + startX) * 4;
	const target: [number, number, number, number] = [data[startIdx], data[startIdx + 1], data[startIdx + 2], data[startIdx + 3]];

	if (fill[0] === target[0] && fill[1] === target[1] && fill[2] === target[2] && fill[3] === target[3]) return;

	const stack: number[] = [startX, startY];
	const visited = new Uint8Array(width * height);

	while (stack.length >= 2) {
		const y = stack.pop() as number;
		const x = stack.pop() as number;

		if (x < 0 || x >= width || y < 0 || y >= height) continue;

		const key = y * width + x;
		if (visited[key]) continue;

		const idx = key * 4;
		if (!colorsMatch(data, idx, target, tolerance)) continue;

		visited[key] = 1;
		data[idx] = fill[0];
		data[idx + 1] = fill[1];
		data[idx + 2] = fill[2];
		data[idx + 3] = fill[3];

		stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
	}

	ctx.putImageData(imageData, 0, 0);
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error('Failed to export canvas'));
		}, 'image/png');
	});
}

/**
 * Rasterize doodle strokes to a white-background PNG `File` (sized to
 * {@link DOODLE_CANVAS}), ready to hand to the host upload seam. Shares the
 * canvas renderer with the editor so the output matches what the modal/preview
 * show.
 */
export async function rasterizeStrokes(strokes: DoodleStroke[]): Promise<File> {
	const exportCanvas = document.createElement('canvas');
	exportCanvas.width = DOODLE_CANVAS.width;
	exportCanvas.height = DOODLE_CANVAS.height;
	const ctx = exportCanvas.getContext('2d');
	if (!ctx) throw new Error('Failed to get 2d canvas context for doodle export');

	ctx.fillStyle = DOODLE_CANVAS_BG;
	ctx.fillRect(0, 0, DOODLE_CANVAS.width, DOODLE_CANVAS.height);

	for (const stroke of strokes) {
		renderStrokeToCtx(ctx, stroke);
	}

	const blob = await canvasToBlob(exportCanvas);
	return new File([blob], `doodle-${Date.now()}.png`, { type: 'image/png' });
}
